import React, { useState, useEffect } from 'react';
import { X, History, User, Calendar, Edit3 } from 'lucide-react';
import { publicationAPI } from '../../services/api';

interface EditHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  publicationId: number;
  publicationTitle: string;
}

interface EditHistoryEntry {
  id: number;
  field: string;
  old_value: string;
  new_value: string;
  edited_at: string;
  edited_by_name: string;
}

const EditHistoryModal: React.FC<EditHistoryModalProps> = ({
  isOpen,
  onClose,
  publicationId,
  publicationTitle
}) => {
  const [editHistory, setEditHistory] = useState<EditHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && publicationId) {
      loadEditHistory();
    }
  }, [isOpen, publicationId]);

  const loadEditHistory = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const history = await publicationAPI.getEditHistory(publicationId);
      setEditHistory(history);
    } catch (error: any) {
      console.error('Error loading edit history:', error);
      // Check if it's a 403 (access denied) error and use the backend message
      if (error?.response?.status === 403) {
        setError(error?.response?.data?.error || 'Only the lead author or co-authors can view this publication\'s edit history.');
      } else {
        setError('Failed to load edit history');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getFieldDisplayName = (field: string) => {
    const fieldMap: { [key: string]: string } = {
      'title': 'Title',
      'abstract': 'Abstract',
      'methods': 'Methods',
      'data_sources': 'Data Sources',
      'stage': 'Stage',
      'status': 'Status'
    };
    return fieldMap[field] || field.charAt(0).toUpperCase() + field.slice(1);
  };

  const formatValue = (value: string, field: string) => {
    if (!value) return <span className="text-gray-400 italic">(empty)</span>;
    
    if (field === 'data_sources') {
      try {
        const sources = JSON.parse(value);
        return Array.isArray(sources) ? sources.join(', ') : value;
      } catch {
        return value;
      }
    }
    
    if (value.length > 200) {
      return (
        <div>
          <div className="text-sm">{value.substring(0, 200)}...</div>
          <div className="text-xs text-gray-500 mt-1">(truncated)</div>
        </div>
      );
    }
    
    return value;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <History className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Edit History</h2>
              <p className="text-sm text-gray-600">{publicationTitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-600 border-t-transparent" />
              <span className="ml-3 text-gray-600">Loading edit history...</span>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-red-600 mb-2">{error}</div>
              {error.includes('lead author or co-authors') ? null : (
                <button
                  onClick={loadEditHistory}
                  className="text-blue-600 hover:text-blue-700 text-sm"
                >
                  Try again
                </button>
              )}
            </div>
          ) : editHistory.length === 0 ? (
            <div className="text-center py-12">
              <History className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Edit History</h3>
              <p className="text-gray-600">This publication hasn't been edited yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {editHistory.map((entry) => (
                <div key={entry.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Edit3 className="h-4 w-4 text-gray-500" />
                      <span className="font-medium text-gray-900">
                        {getFieldDisplayName(entry.field)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <User className="h-3 w-3" />
                        <span>{entry.edited_by_name}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(entry.edited_at)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">Previous Value:</div>
                      <div className="text-sm text-gray-600 bg-red-50 p-3 rounded border-l-4 border-red-200">
                        {formatValue(entry.old_value, entry.field)}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-700 mb-1">New Value:</div>
                      <div className="text-sm text-gray-600 bg-green-50 p-3 rounded border-l-4 border-green-200">
                        {formatValue(entry.new_value, entry.field)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditHistoryModal;
